"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Loader2, RotateCcw } from "lucide-react"
import { useAgentRolesQuery } from "@/hooks/queries/ai-use-case-roles"
import { useComposeAgentRolesMutation } from "@/hooks/mutations/ai-use-case-roles"
import {
  AGENT_PRIMARY_ROLE_USE_CASE,
  DEFAULT_ROLE_SLUG,
  composeClassifierLabels,
  isCustomOnly,
  roleSettingsSchemaFor,
  type AgentRoleScopeType,
} from "@/lib/ai/roles/types"
import type { ComposedUseCaseConfig } from "@/lib/ai/roles/compose"
import { useTranslations } from "next-intl"



interface ResetToRoleDefaultButtonProps {
  /** Channel the agent belongs to — decides which use cases a composition covers. */
  scopeType: AgentRoleScopeType
  useCase: string
  /** The agent's role slugs (config.roles); falls back to 'custom' like apply does. */
  roleSlugs: string[] | undefined
  /** Tenant-invented custom-role name (config.role_label) — shown instead of "Custom". */
  roleLabel?: string | null
  onReset: (composed: ComposedUseCaseConfig) => void
  disabled?: boolean
}

/**
 * Fills a prompt field with what the agent's roles would apply, after a confirm;
 * saving stays explicit. A single role resolves from the preset locally — free
 * and instant. Two or more have to be merged by the model, which costs a call,
 * so the confirm says as much.
 */
export function ResetToRoleDefaultButton({
  scopeType,
  useCase,
  roleSlugs,
  roleLabel,
  onReset,
  disabled,
}: ResetToRoleDefaultButtonProps) {

  const tUi = useTranslations("AgentTools.AgentsSharedResetToRoleDefaultButton")
  const tCommonUi = useTranslations("Common")

  const { data: roles = [] } = useAgentRolesQuery(useCase)
  const compose = useComposeAgentRolesMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const slugs = roleSlugs?.length ? roleSlugs : [DEFAULT_ROLE_SLUG]
  const isSingle = slugs.length === 1

  // Same rule as the server resolver: Custom stands in for a SECONDARY use case
  // the role does not define, never for the primary one — a deleted primary preset
  // must not silently reset the agent to the generic starter.
  const isPrimaryUseCase = AGENT_PRIMARY_ROLE_USE_CASE[scopeType] === useCase
  const exact = roles.find((r) => r.slug === slugs[0])
  const preset = exact ?? (isPrimaryUseCase ? undefined : roles.find((r) => r.slug === DEFAULT_ROLE_SLUG))

  if (isSingle && !preset) return null

  const displayName = isCustomOnly(slugs)
    ? roleLabel?.trim() || preset?.name || "Custom"
    : slugs
        .map((slug) => roles.find((r) => r.slug === slug)?.name ?? slug)
        .join(" + ")

  const applyReset = async () => {
    setError(null)
    if (isSingle && preset) {
      // Through the same normalization apply uses, or the preview would show
      // labels the server would then store differently.
      onReset({
        system_prompt: preset.system_prompt,
        settings: roleSettingsSchemaFor(useCase).parse(
          "classifier_labels" in preset.settings
            ? { classifier_labels: composeClassifierLabels([preset]) }
            : preset.settings,
        ),
      })
      setConfirmOpen(false)
      return
    }
    try {
      const bundle = await compose.mutateAsync({ scope_type: scopeType, role_slugs: slugs })
      const composed = bundle[useCase]
      if (!composed) throw new Error(tUi("failedToComposeThePrompt"))
      onReset(composed)
      setConfirmOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToComposeThePrompt"))
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-muted-foreground"
        disabled={disabled}
        onClick={() => {
          setError(null)
          setConfirmOpen(true)
        }}
      >
        <RotateCcw className="h-3.5 w-3.5" />
        <span key={tUi("resetToRoleDefault", { role: displayName })}>{tUi("resetToRoleDefault", { role: displayName })}</span>
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => !compose.isPending && setConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("resetRoleConfirmation", { role: displayName })}>{tUi("resetRoleConfirmation", { role: displayName })}</span></AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block" key={tUi("thisReplacesTheCurrentContentWithWhatThe")}>{tUi("thisReplacesTheCurrentContentWithWhatThe")}</span>
              {!isSingle && <span className="block" key={tUi("mergingRolesNotice")}>{tUi("mergingRolesNotice")}</span>}
              <span className="block" key={tUi("nothingIsStoredUntilYouSaveExplicitly")}>{tUi("nothingIsStoredUntilYouSaveExplicitly")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={compose.isPending}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              disabled={compose.isPending}
              onClick={(event) => {
                // Radix closes the dialog on click; keep it open while composing.
                event.preventDefault()
                void applyReset()
              }}
            >
              {compose.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              <span><span key={tUi("reset")}>{tUi("reset")}</span></span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
